import { useEffect, useMemo } from "react";
import { useBookContext } from "../context/BookContext";
import { useGeneralContext } from "../context/AppGeneralContext";
import { useRTContext } from "../context/RTContext";
import { useTestCreationContext } from "../context/TestCreationContext";
import {
    getActivityBook,
    hasTestVocabulary,
    hasTrainingCards,
    mapVocabTrainingToActivity,
    mapTestToActivity,
} from "../helpers/ClassActivityMapping";
import { useActivityBookAvailability } from "./useActivityBookAvailability";
import type { ClassActivity } from "../complex/classActivityCard/ClassActivityCardsOverview.types";
import { useTestOperations } from "./useTestOperations";
import { useVocabTrainingOperations } from "./useVocabTrainingOperations";

interface UseClassActivityDataOptions {
    /**
     * Defaults to true. Only covers this hook's own indicator; loadUserTests still shows the
     * global one on its first load.
     */
    useGlobalLoadingIndicator?: boolean;
    /**
     * Skip the trainings refetch when this provider instance already holds them (e.g. the
     * existence probe ran on the same page).
     */
    reuseLoadedTrainings?: boolean;
    /**
     * Keep only what the current flow can offer as a vocabulary source: activities with words, from
     * a book the flow allows. The overviews leave this off — they list everything the teacher has.
     */
    onlySelectableActivities?: boolean;
}

export const useClassActivityData = (schoolClassId?: string, options: UseClassActivityDataOptions = {}) => {
    const useGlobalLoadingIndicator = options.useGlobalLoadingIndicator ?? true;
    const reuseLoadedTrainings = options.reuseLoadedTrainings ?? false;
    const onlySelectableActivities = options.onlySelectableActivities ?? false;
    const { userInfo, userId, setIsDataBeingLoaded } = useGeneralContext();
    const { schoolClasses, reloadSchoolClasses } = useRTContext();
    const { userUsedBooksInfo } = useBookContext();
    const {
        userTests,
        loadUserTests,
        dataLoaded: testDataLoaded,
        userTestsLoadError,
        studentTestContentWrapper,
        userUsedOwnSubjectsInfo,
    } = useTestCreationContext();

    const testOps = useTestOperations(schoolClassId);
    const vocabTrainingOps = useVocabTrainingOperations(schoolClassId);

    const isTeacher = userInfo.visitorOrUserRole?.toLowerCase() === "teacher";

    useEffect(() => {
        const loadActivities = async () => {
            if (!userId || !isTeacher) return;

            if (useGlobalLoadingIndicator) setIsDataBeingLoaded(true);

            const promises: Promise<unknown>[] = [];
            if (!testDataLoaded) promises.push(loadUserTests());
            // Refetch trainings by default: their state lives in RTContext, which stays mounted across
            // navigation, and trainings created under /create go through a second provider instance.
            if (reuseLoadedTrainings && vocabTrainingOps.vocabTrainingsDataLoaded) {
                vocabTrainingOps.resolveVocabTrainingInfos(vocabTrainingOps.vocabTrainings);
            } else {
                promises.push(vocabTrainingOps.loadVocabTrainings());
            }
            if (schoolClasses.length === 0) promises.push(reloadSchoolClasses());
            await Promise.all(promises);

            if (useGlobalLoadingIndicator) setIsDataBeingLoaded(false);
        };

        loadActivities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, isTeacher]);

    const areListsLoaded = testDataLoaded && vocabTrainingOps.vocabTrainingsDataLoaded;

    const { areBookInfosResolved, isBookOfActivityBlocked } = useActivityBookAvailability(
        userTests.filter(hasTestVocabulary),
        vocabTrainingOps.vocabTrainings.filter(hasTrainingCards),
        onlySelectableActivities && areListsLoaded
    );

    const allDataLoaded = areListsLoaded && (!onlySelectableActivities || areBookInfosResolved);

    const activities = useMemo((): ClassActivity[] => {
        if (!allDataLoaded) return [];

        const testActivities: ClassActivity[] = userTests.map((test) =>
            mapTestToActivity({
                test,
                handleEditTest: testOps.handleEditTest,
                handlePrimaryAction: testOps.handleShareButtonClick,
                handleDuplicateTest: testOps.handleOpenDuplicateTestPopup,
                handleDeleteTest: testOps.handleOpenDeleteTestPopup,
                schoolClassId,
            })
        );

        const vocabTrainingActivities: ClassActivity[] = vocabTrainingOps.vocabTrainings.map((training) =>
            mapVocabTrainingToActivity({
                vocabTraining: training,
                userUsedBooksInfo,
                userUsedOwnSubjectsInfo,
                handlePrimaryAction: vocabTrainingOps.handleShareButtonClick,
                handleDuplicateVocabTraining: vocabTrainingOps.handleOpenDuplicateVocabTrainingPopup,
                handleOpenDeleteVocabTrainingModal: vocabTrainingOps.handleOpenDeleteVocabTrainingModal,
                schoolClassId,
            })
        );

        const allActivities = [...testActivities, ...vocabTrainingActivities].sort((a, b) => b.sortDate - a.sortDate);
        if (!onlySelectableActivities) return allActivities;

        return allActivities.filter(
            (activity) => activity.vocabCount > 0 && !isBookOfActivityBlocked(getActivityBook(activity, userId)?.id)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        allDataLoaded,
        userTests,
        vocabTrainingOps.vocabTrainings,
        userUsedBooksInfo,
        userUsedOwnSubjectsInfo,
        studentTestContentWrapper,
        onlySelectableActivities,
    ]);

    return {
        activities,
        allDataLoaded,
        testOps,
        vocabTrainingOps,
        schoolClasses,
        studentTestContentWrapper,
        userTests,
        userTestsLoadError,
        userUsedBooksInfo,
        userUsedOwnSubjectsInfo,
    };
};
